import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { BootstrapTooltip } from "../../../../components/mui/BootstrapTooltip";
import FlexBox from "../../../../components/layouts/flexbox";
import { SectionTitle } from "./styles";
import {
  getActionLinkActorLabel,
  useInvitationActionLink,
} from "./useInvitationActionLink";

const COPY_FEEDBACK_MS = 2000;

interface InvitationShareLinkProps {
  ambassadorInvitationId?: string;
  refreshKey?: string;
  enabled?: boolean;
}

function InvitationShareLink({
  ambassadorInvitationId,
  refreshKey,
  enabled = false,
}: InvitationShareLinkProps) {
  const { linkData, loading } = useInvitationActionLink(
    ambassadorInvitationId,
    refreshKey,
    enabled
  );
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const handleCopy = useCallback(() => {
    if (!linkData?.link) return;

    navigator.clipboard.writeText(linkData.link);
    setCopied(true);

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    copyTimeoutRef.current = setTimeout(
      () => setCopied(false),
      COPY_FEEDBACK_MS
    );
  }, [linkData?.link]);

  if (!enabled) return null;
  if (!loading && !linkData) return null;

  return (
    <Box>
      <Divider sx={{ my: 1 }} />
      <SectionTitle>Share Link</SectionTitle>

      {loading && (
        <FlexBox alignItems="center" gap={0.75} py={0.5}>
          <CircularProgress size={14} thickness={5} />
          <Typography fontSize={11} color="#64748B">
            Fetching share link...
          </Typography>
        </FlexBox>
      )}

      {!loading && linkData && (
        <Box
          py={0.75}
          pl={1}
          pr={0.75}
          borderLeft="3px solid #2563EB"
          bgcolor="#F8FAFC"
          borderRadius="0 4px 4px 0"
        >
          <FlexBox alignItems="center" justifyContent="space-between" gap={1}>
            <Box flex={1} minWidth={0}>
              {linkData.action ? (
                <>
                  <Typography
                    fontSize={11}
                    fontWeight={600}
                    color="#1F2937"
                    lineHeight={1.45}
                  >
                    Next action ({getActionLinkActorLabel(linkData.actor)}):
                  </Typography>
                  <Typography fontSize={11} lineHeight={1.45} color="#374151">
                    {linkData.action}
                  </Typography>
                </>
              ) : (
                <Typography
                  fontSize={11}
                  fontWeight={600}
                  color="#1F2937"
                  lineHeight={1.45}
                >
                  Share link
                </Typography>
              )}
            </Box>

            <BootstrapTooltip
              title={copied ? "Copied" : "Copy link"}
              open={copied ? true : undefined}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                onClick={handleCopy}
                aria-label="Copy link"
                sx={{ p: 0.25, flexShrink: 0 }}
              >
                <LinkOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: copied ? "#059669" : "#2563EB",
                    transform: "rotate(45deg)",
                  }}
                />
              </IconButton>
            </BootstrapTooltip>
          </FlexBox>
        </Box>
      )}
    </Box>
  );
}

export { InvitationShareLink };
